import { ButtonComponent, ChipComponent, InputComponent } from '@accesso/ng-uds';
import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { TagService } from '../../core/services/tag.service';
import { NavigationProduct, Tag } from '../../shared/models/product.model';
import { ProductCardItemComponent } from './product-card-item/product-card-item.component';

@Component({
  selector: 'app-product-cards',
  templateUrl: './product-cards.component.html',
  styleUrls: ['./product-cards.component.scss'],
  standalone: true,
  imports: [CommonModule, ProductCardItemComponent, InputComponent, PaginatorModule, ButtonComponent, FormsModule, ChipComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductCardsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() navigationProducts: NavigationProduct[] = [];
  filteredProducts: NavigationProduct[] = [];
  displayedProducts: NavigationProduct[] = [];
  searchQuery = '';
  isSearchBoxScrolled = false;

  // For debounced search
  searchSubject = new Subject<string>();
  searchSubscription: Subscription | null = null;

  // Tag-related properties
  tags: Tag[] = [];
  selectedTagIds: number[] = [];
  productsWithTags: NavigationProduct[] = [];

  // For card layout
  isCompactView = false;

  // Tag count - track how many products have each tag
  tagCounts = new Map<number, number>();

  @ViewChild('searchInput') searchInput!: InputComponent;
  @ViewChild('productContainer') productContainer!: ElementRef;
  @ViewChild('searchBox') searchBox!: ElementRef;

  // Pagination variables
  rows = 10;
  first = 0;
  totalRecords = 0;

  @Output() selectedProduct = new EventEmitter<NavigationProduct>();

  constructor(private tagService: TagService) {}

  ngOnInit(): void {
    // Initialize debounced search (300ms delay)
    this.searchSubscription = this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe((query) => {
      this.searchQuery = query;
      this.applyFilters();
      this.first = 0; // Reset to first page
      this.updateDisplayedProducts();
    });

    // Process products to extract tags and apply colors
    this.tagService.processProductsWithTags(this.navigationProducts).subscribe((productsWithTags) => {
      this.productsWithTags = productsWithTags;

      // Initialize filtered products with all navigation products that have tags
      this.filteredProducts = [...this.productsWithTags];
      this.totalRecords = this.filteredProducts.length;
      this.updateDisplayedProducts();

      // Calculate tag counts
      this.calculateTagCounts();
    });

    // Get all available tags after processing
    this.tagService.getAllTags().subscribe((tags) => {
      this.tags = tags;
    });
  }

  ngOnChanges(): void {
    // Update filtered products when input products change
    if (this.navigationProducts.length > 0) {
      // Reprocess products with tags when input changes
      this.tagService.processProductsWithTags(this.navigationProducts).subscribe((productsWithTags) => {
        this.productsWithTags = productsWithTags;
        this.filteredProducts = [...this.productsWithTags];
        this.totalRecords = this.filteredProducts.length;

        this.applyFilters();

        this.calculateTagCounts();
      });

      // Update available tags
      this.tagService.getAllTags().subscribe((tags) => {
        this.tags = tags;
      });
    }
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  navigateToProduct(product: NavigationProduct): void {
    this.selectedProduct.emit(product);
  }

  onSearch(event: Event): void {
    const query = event.target ? (event.target as HTMLInputElement).value.toLowerCase().trim() : '';
    this.searchSubject.next(query);
  }

  toggleTag(tagId: number): void {
    // If tag is already selected, remove it; otherwise, add it
    if (this.selectedTagIds.includes(tagId)) {
      this.selectedTagIds = this.selectedTagIds.filter((id) => id !== tagId);
    } else {
      this.selectedTagIds.push(tagId);
    }

    // Apply filters with the updated selected tags
    this.applyFilters();

    // Reset to first page when tag selection changes
    this.first = 0;
    this.updateDisplayedProducts();
  }

  // Get the tag object by ID
  getTagById(tagId: number): Tag | undefined {
    return this.tags.find((tag) => tag.id === tagId);
  }

  isTagSelected(tagId: number): boolean {
    return this.selectedTagIds.includes(tagId);
  }

  getTagCount(tagId: number): number {
    return this.tagCounts.get(tagId) || 0;
  }

  // Get selected tags as Tag objects
  getSelectedTags(): Tag[] {
    return this.selectedTagIds.map((id) => this.getTagById(id)).filter((tag) => tag !== undefined) as Tag[];
  }

  removeSelectedTag(tagId: number): void {
    this.selectedTagIds = this.selectedTagIds.filter((id) => id !== tagId);
    this.applyFilters();
    this.first = 0;
    this.updateDisplayedProducts();
  }

  // Toggle between compact and comfortable view
  toggleCardDensity(): void {
    this.isCompactView = !this.isCompactView;
  }

  // Scroll back to top
  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Helper method to clear search from template
  clearSearch(): void {
    if (this.searchInput) {
      this.searchInput.writeValue('');
    }
    this.searchSubject.next('');
  }

  private calculateTagCounts(): void {
    this.tagCounts.clear();

    // Count products for each tag
    this.productsWithTags.forEach((product) => {
      if (product.tags) {
        product.tags.forEach((tag) => {
          const currentCount = this.tagCounts.get(tag.id) || 0;
          this.tagCounts.set(tag.id, currentCount + 1);
        });
      }
    });
  }

  clearFilter(): void {
    this.searchQuery = '';
    this.selectedTagIds = [];

    if (this.searchInput) {
      this.searchInput.writeValue('');
    }

    // Reset filtering
    this.filteredProducts = [...this.productsWithTags];
    this.totalRecords = this.filteredProducts.length;
    this.first = 0;
    this.updateDisplayedProducts();
  }

  private applyFilters(): void {
    // Start with all products that have tags
    let filtered = [...this.productsWithTags];

    // Apply text search if there is a query
    if (this.searchQuery) {
      filtered = filtered.filter((product) => product.name.toLowerCase().includes(this.searchQuery.toLowerCase()));
    }

    // Apply tag filtering if there are selected tags
    if (this.selectedTagIds.length > 0) {
      filtered = filtered.filter((product) => product.tags && product.tags.some((tag) => this.selectedTagIds.includes(tag.id)));
    }

    this.filteredProducts = filtered;
    this.totalRecords = this.filteredProducts.length;
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    this.updateDisplayedProducts();
  }

  private updateDisplayedProducts(): void {
    // Get subset of products for current page
    this.displayedProducts = this.filteredProducts.slice(this.first, this.first + this.rows);
  }
}
